import { useEffect } from 'react'
import { useCart } from '@/hooks/useCart'

interface SlideMenuProps {
  open: boolean
  onClose: () => void
}

/*
  Figma: node 249:630
  Menu container: black bg, left: var(--chrome-sidebar-width) (beside
  sidebar), top: var(--chrome-top-nav-height) (below top nav). Fills
  remaining viewport. Vertical panel padding only (32px); horizontal
  padding lives on each item so the 1px white dividers extend edge-to-edge.
  Nav links: Poppins Light 70px, white, uppercase.
  Animation: slides in left-to-right (opening), right-to-left (closing).

  Layout: every menu row is a flex child with `flex: 1` so the seven items
  (HOME, ARTISTS, MARKETPLACE, EXHIBITIONS, ABOUT US, CONTACT, CART) split
  the panel height evenly. Dividers are rendered via CSS `:nth-child(n+2)`
  so every link except the first gets a `border-top`. Items have a
  `min-height` so the layout scrolls instead of crushing text on very
  short viewports.

  CART entry shows the live cart count from the shared useCart store:
  "CART" when empty, "CART (n)" otherwise. The count refreshes whenever
  the menu opens.

  All styling lives in src/styles/components/slide-menu.css; the
  open/close state is driven via `data-open` on the wrapper and panel.
*/

/* comingSoon items render greyed-out with a "(Coming Soon)" tag and no
   href, so they are neither clickable nor keyboard-focusable. */
const NAV_ITEMS: Array<{ label: string; href: string; comingSoon?: boolean }> = [
  { label: 'HOME', href: '/' },
  { label: 'ARTISTS', href: '/artists', comingSoon: true },
  { label: 'MARKETPLACE', href: '/marketplace', comingSoon: true },
  { label: 'EXHIBITIONS', href: '/exhibitions', comingSoon: true },
  { label: 'ABOUT US', href: '/about-us' },
  { label: 'CONTACT', href: '/contact' },
]

export default function SlideMenu({ open, onClose }: SlideMenuProps) {
  const { itemCount, refresh } = useCart()

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      refresh()
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open, refresh])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <>
      {/* Wrapper creates the clipping region so the menu slides cleanly without bleeding */}
      <div className="mig-menu-wrapper" data-open={open}>
        {/* Menu panel - Figma: node 249:843, black bg, slides left-to-right (open), right-to-left (close) */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="mig-menu-panel"
          data-open={open}
        >
          <nav className="mig-menu-nav" aria-label="Primary">
            {NAV_ITEMS.map((item) =>
              item.comingSoon ? (
                <a
                  key={item.href}
                  className="menu-nav-link"
                  data-disabled="true"
                  aria-disabled="true"
                >
                  {item.label}
                  <span className="menu-nav-coming-soon">(Coming Soon)</span>
                </a>
              ) : (
                <a key={item.href} href={item.href} className="menu-nav-link">
                  {item.label}
                </a>
              ),
            )}
            <a
              href="/cart"
              className="menu-nav-link menu-nav-link--cart"
              aria-label={`Cart${itemCount > 0 ? `, ${itemCount} item${itemCount === 1 ? '' : 's'}` : ', empty'}`}
            >
              CART{itemCount > 0 ? ` (${itemCount})` : ''}
            </a>
          </nav>
        </div>
      </div>
    </>
  )
}
